import { useRef, type KeyboardEvent, type ReactNode } from 'react';

/**
 * A single-select segmented control — the app's one keyboard-correct switch.
 *
 * `role="radiogroup"` with a roving tabindex, so the whole control is ONE tab
 * stop and the arrow keys move between options. That is the correct pattern for
 * a set of mutually exclusive choices, and it is what the hand-rolled year
 * pickers and tab strips around the codebase were each re-implementing
 * differently.
 *
 * Selection follows focus (moving with an arrow key selects), which is right
 * for a filter where every option is cheap to preview.
 */

export interface SegmentedOption<T extends string | number> {
  value: T;
  /** The word. Never rely on colour or a glyph alone to identify an option. */
  label: ReactNode;
  /** Accessible name, when `label` is not plain text. */
  ariaLabel?: string;
  /** Trailing content — a score, a count, a warning glyph. */
  trailing?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string | number> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Required — a bare group of buttons announces as nothing useful. */
  ariaLabel: string;
  size?: 'sm' | 'md';
  /** "solid" is a filled pill track; "underline" matches the page tab bars. */
  variant?: 'solid' | 'underline';
  className?: string;
}

export default function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
  ariaLabel,
  size = 'md',
  variant = 'solid',
  className = '',
}: SegmentedControlProps<T>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const enabled = options.filter((o) => !o.disabled);

  const moveTo = (option: SegmentedOption<T> | undefined) => {
    if (!option) return;
    const index = options.findIndex((o) => o.value === option.value);
    onChange(option.value);
    refs.current[index]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const position = enabled.findIndex((o) => o.value === value);
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        moveTo(enabled[(position + 1 + enabled.length) % enabled.length]);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        moveTo(enabled[(position - 1 + enabled.length) % enabled.length]);
        break;
      case 'Home':
        event.preventDefault();
        moveTo(enabled[0]);
        break;
      case 'End':
        event.preventDefault();
        moveTo(enabled[enabled.length - 1]);
        break;
      default:
        break;
    }
  };

  const pad = size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3.5 py-1.5 text-sm';

  if (variant === 'underline') {
    return (
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        className={`flex flex-wrap gap-1 border-b border-neutral-200 ${className}`}
      >
        {options.map((option, index) => {
          const active = option.value === value;
          return (
            <button
              key={String(option.value)}
              ref={(el) => {
                refs.current[index] = el;
              }}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={option.ariaLabel}
              disabled={option.disabled}
              // Roving tabindex: only the selected option is in the tab order.
              tabIndex={active ? 0 : -1}
              onClick={() => !option.disabled && onChange(option.value)}
              onKeyDown={handleKeyDown}
              className={`-mb-px flex items-center gap-2 border-b-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-not-allowed disabled:opacity-40 ${pad} ${
                active
                  ? 'border-primary-600 font-semibold text-primary-700'
                  : 'border-transparent font-medium text-neutral-500 hover:border-neutral-300 hover:text-neutral-800'
              }`}
            >
              {option.label}
              {option.trailing}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`inline-flex flex-wrap items-center gap-0.5 rounded-lg border border-neutral-200 bg-neutral-50 p-0.5 ${className}`}
    >
      {options.map((option, index) => {
        const active = option.value === value;
        return (
          <button
            key={String(option.value)}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={option.ariaLabel}
            disabled={option.disabled}
            tabIndex={active ? 0 : -1}
            onClick={() => !option.disabled && onChange(option.value)}
            onKeyDown={handleKeyDown}
            className={`flex items-center gap-2 rounded-md transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500 disabled:cursor-not-allowed disabled:opacity-40 ${pad} ${
              active
                ? 'bg-white font-semibold text-primary-700 shadow-sm'
                : 'font-medium text-neutral-500 hover:text-neutral-800'
            }`}
          >
            {option.label}
            {option.trailing}
          </button>
        );
      })}
    </div>
  );
}
